/**
 * Zod schemas for /api/search/shop endpoint
 * Intelligent keyword-to-tag search using semantic embeddings
 */

import { z } from 'zod';
import {
  TagSearchListingSchema,
  TagMetadataSchema,
} from './tags';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const ShopSearchRequestSchema = z.object({
  keywords: z.string().min(1, 'Keywords are required'),
  maxTags: z.coerce.number().int().positive().max(20).optional().default(8),
  listingsPerTag: z.coerce.number().int().positive().max(50).optional().default(16),
  excludeTags: z.string().optional().default(''),
});

export type ShopSearchRequest = z.infer<typeof ShopSearchRequestSchema>;

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

export const MatchedTagSchema = z.object({
  tag: z.string(),
  matchType: z.enum(['exact', 'semantic', 'text']),
  score: z.number(),
});

export type MatchedTag = z.infer<typeof MatchedTagSchema>;

/** The tag the keywords named exactly, with what the page heading needs from it. */
export const ShopExactTagSchema = z.object({
  tag: z.string(),
  /** utils/tag-axis.ts: only a product-type tag is pluralized in the heading. */
  axis: z.string(),
  /** The category group nearly all its listings sit in, or null when they spread. */
  categoryGroup: z.string().nullable(),
});

export type ShopExactTag = z.infer<typeof ShopExactTagSchema>;

export const ShopSearchSuccessResponseSchema = z.object({
  success: z.literal(true),
  searchPhrase: z.string(),
  matchedTags: z.array(MatchedTagSchema),
  exactTag: ShopExactTagSchema.optional(),
  listings: z.array(TagSearchListingSchema),
  tagMetadata: z.record(z.string(), TagMetadataSchema).optional(),
  groupCounts: z.record(z.string(), z.number()),
  total: z.number(),
  hasMore: z.boolean(),
});

export type ShopSearchSuccessResponse = z.infer<typeof ShopSearchSuccessResponseSchema>;

export const ShopSearchErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export type ShopSearchErrorResponse = z.infer<typeof ShopSearchErrorResponseSchema>;

export const ShopSearchResponseSchema = z.union([
  ShopSearchSuccessResponseSchema,
  ShopSearchErrorResponseSchema,
]);

export type ShopSearchResponse = z.infer<typeof ShopSearchResponseSchema>;
